import React, { useState } from 'react';
import styles from './PrequalificationStep.module.css';
import { axisBranding } from './branding';

interface PrequalificationStepProps {
  onBack: () => void;
  onProceed: () => void;
  initialAnswers?: Record<number, string>;
  onAnswersChange?: (answers: Record<number, string>) => void;
}

// Hardcoded questions to match the Figma mock
const QUESTIONS = [
  "Have you ever been diagnosed with a neurocognitive disease such as Alzheimer’s, lewy body dementia, or schizophrenia?",
  "Are you looking for a provider who can prescribe Suboxone, Methadone, or any opioid pain medication?",
  "Do you have Kaiser insurance?"
];

const PrequalificationStep: React.FC<PrequalificationStepProps> = ({ 
  onBack, 
  onProceed, 
  initialAnswers = {}, 
  onAnswersChange 
}) => {
  const [answers, setAnswers] = useState<Record<number, string>>(initialAnswers);

  // Only allow proceed if all answers are 'No'
  const allAllowed = QUESTIONS.every((_, index) => answers[index] === 'No');

  const handleChange = (questionIndex: number, value: string) => {
    const newAnswers = { ...answers, [questionIndex]: value };
    setAnswers(newAnswers);
    // Notify parent component of changes
    if (onAnswersChange) {
      onAnswersChange(newAnswers);
    }
  };

  return (
    <div className={styles.preQualificationAssessment}>
      {/* Header/Navigation */}
      <div className={styles.navBar}>
        <span className={styles.navPhone}>{axisBranding.phoneNumber}</span>
        <div className={styles.navLinks}>
          <button className={styles.navLink} onClick={() => window.location.reload()}>HOME</button>
        </div>
        {/* <div className={styles.navMenuIcon}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12h18M3 6h18M3 18h18" stroke="white" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </div> */}
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <h1 className={styles.title}>Pre-qualification Assessment</h1>
        <p className={styles.subtitle}>
          Please answer the following questions honestly to ensure we can provide you with the appropriate care.
        </p>

        <div className={styles.card}>
          <div className={styles.sectionTitle}>Health and Safety Screening</div>
          <div className={styles.sectionSubtitle}>
            This brief assessment helps us determine if online booking is appropriate for your current needs.
          </div>

          <div className={styles.questionsList}>
            {QUESTIONS.map((question, index) => (
              <div key={index} className={styles.questionItem}>
                <div className={styles.questionText}>
                  <span className={styles.questionNumber}>{index + 1}.</span>
                  {question}
                </div>
                <div className={styles.radioGroup}>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      name={`question-${index}`}
                      value="Yes"
                      checked={answers[index] === 'Yes'}
                      onChange={() => handleChange(index, 'Yes')}
                      className={styles.radioInput}
                    />
                    <span className={styles.radioLabel}>Yes</span>
                  </label>
                  <label className={styles.radioOption}>
                    <input
                      type="radio"
                      name={`question-${index}`}
                      value="No"
                      checked={answers[index] === 'No'}
                      onChange={() => handleChange(index, 'No')}
                      className={styles.radioInput}
                    />
                    <span className={styles.radioLabel}>No</span>
                  </label>
                </div>
              </div>
            ))}
          </div>

          <button
            className={styles.proceedButton}
            onClick={onProceed}
            disabled={!allAllowed}
          >
            Proceed to Patient Information
          </button>
        </div>

        <button className={styles.backButton} onClick={onBack}>
          ← Back
        </button>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerTitle}>{axisBranding.organizationName}</div>
        <div className={styles.footerInfo}>
          <div className={styles.footerInfoItem}>
            <span>📞</span>
          <span>Phone (appointments): {axisBranding.phoneNumber}</span>
          </div>
          <div className={styles.footerInfoItem}>
            <span>📍</span>
          </div>
        </div>
        <div className={styles.rating}>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
          <span className={styles.star}>★</span>
        </div>
        <div className={styles.ratingText}>Average Rating: 4.9/5</div>
      </div>

      {/* Accessibility Icon */}
      <div className={styles.accessibilityIcon}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="4" r="2" fill="white"/>
          <path d="M12 6v8M8 14h8M12 18v4M8 22h8" stroke="white" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
    </div>
  );
};

export default PrequalificationStep; 